import { BehaviorSubject, Observable, of } from 'rxjs';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnInit,
} from '@angular/core';
import {
    Dialog,
    NotificationService,
    SelectionManager,
} from '@vendure/admin-ui/core';
import {
    debounceTime,
    distinctUntilChanged,
    finalize,
    map,
    switchMap,
} from 'rxjs/operators';

import { FormControl } from '@angular/forms';
import { ShoppingListService } from '../../shopping-list.service';
import { TranslateService } from '@ngx-translate/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';

@Component({
    standalone: false,
    selector: 'vdr-assign-customer-to-shopping-list-dialog',
    templateUrl: './assign-customer-to-shopping-list-dialog.component.html',
    styleUrls: ['./assign-customer-to-shopping-list-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignCustomerToShoppingListDialogComponent
    implements OnInit, Dialog<any>
{
    items$: Observable<any[]>;
    totalItems$ = new BehaviorSubject<number>(0);
    currentPage$ = new BehaviorSubject<number>(1);
    itemsPerPage$ = new BehaviorSubject<number>(10);

    searchTermControl = new FormControl('');
    selectionManager: SelectionManager<any>;

    resolveWith: (result?: any) => void;

    // assigned by ModalService.fromComponent() call
    customerIds: string[];

    constructor(
        private cdr: ChangeDetectorRef,
        private translateService: TranslateService,
        private shoppingListService: ShoppingListService,
        private notificationService: NotificationService
    ) {
        this.selectionManager = new SelectionManager({
            multiSelect: true,
            additiveMode: true,
            itemsAreEqual: (a, b) => a.id === b.id,
        });

        this.items$ = this.shoppingListService.shoppingLists$;
    }

    ngOnInit() {
        this.loadShoppingLists();
        this.initializeSearchSubscription();

        this.totalItems$ = new BehaviorSubject<number>(0);
        this.shoppingListService.totalItemsShoppingList$.subscribe((total) => {
            (this.totalItems$ as BehaviorSubject<number>).next(total);
        });
    }

    loadShoppingLists(skip: number = 0, take: number = 10): void {
        const searchTerm = this.searchTermControl.value?.trim() || '';
        this.shoppingListService
            .searchShoppingLists(searchTerm, skip, take)
            .subscribe({
                next: (data) => {
                    this.items$ = of(data.items);
                    this.totalItems$.next(data.totalItems);
                },
                error: (error) => {
                    console.error('Erro ao carregar listas de compras:', error);
                },
            });
    }

    private initializeSearchSubscription(): void {
        this.searchTermControl.valueChanges
            .pipe(
                debounceTime(500),
                distinctUntilChanged(),
                map((term) => term?.trim() || ''),
                switchMap((term) =>
                    this.shoppingListService.searchShoppingLists(
                        term,
                        0,
                        this.itemsPerPage$.getValue()
                    )
                )
            )
            .subscribe({
                next: (data) => {
                    this.items$ = of(data.items);
                    this.totalItems$.next(data.totalItems);
                    this.cdr.markForCheck();
                },
                error: (error) => {
                    console.error('Erro ao buscar lista de compras:', error);
                },
            });
    }

    toggleSelection(shoppingList: any) {
        this.selectionManager.toggleSelection(shoppingList);
    }

    async assign(): Promise<void> {
        const userIdsFromCustomers = this.customerIds;
        const selectedShoppingLists = this.selectionManager.selection;

        if (!selectedShoppingLists.length) {
            this.notificationService.error(
                'shopping-list.actions.no-items-selected'
            );
            return;
        }

        const shoppingListIds = selectedShoppingLists.map(
            (shoppingList) => shoppingList.id
        );

        const operation =
            this.shoppingListService.associateAllBuyersWithShoppingList({
                userIdsFromCustomers,
                shoppingListIds,
            });

        operation
            .pipe(
                finalize(() => {
                    this.resolveWith(true);
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: (result) => {
                    if (
                        result?.__typename ===
                        'AssociateAllBuyersWithShoppingListError'
                    ) {
                        this.notificationService.error(result.message);
                        return;
                    }

                    this.translateService
                        .get('shopping-list.assign-customer.assign-success')
                        .subscribe((message) => {
                            this.notificationService.success(message);
                        });
                },
                error: (error) => {
                    this.translateService
                        .get('shopping-list.assign-customer.assign-error')
                        .subscribe((message) => {
                            this.notificationService.error(message);
                        });
                },
            });

        this.resolveWith(true);
    }

    cancel() {
        this.resolveWith();
    }

    setPageNumber(page: number): void {
        const itemsPerPage = this.itemsPerPage$.getValue();
        const skip = (page - 1) * itemsPerPage;

        this.currentPage$.next(page);
        this.loadShoppingLists(skip, itemsPerPage);
    }

    setItemsPerPage(items: number): void {
        this.itemsPerPage$.next(items);
        this.currentPage$.next(1);
        this.loadShoppingLists(0, items);
    }
}
